import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Descriptions,
  Image,
  App,
  Spin,
  Carousel,
  Badge,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

const PropertyDetail = () => {
  const [property, setProperty] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState<string[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { message } = App.useApp();

  const fetchProperty = useCallback(async () => {
    try {
      const response = await api.get(`properties/${id}`);
      const propertyData = response.data.data;
      setProperty(propertyData);

      if (propertyData.images) {
        try {
          const imageList = JSON.parse(propertyData.images);
          setImages(imageList);
        } catch (e) {
          console.error('Failed to parse images:', e);
          setImages([]);
        }
      }
    } catch (error: any) {
      console.error('Failed to fetch property:', error);
      message.error('Gagal memuat data properti');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, message]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      available: 'green',
      sold: 'red',
      reserved: 'orange',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: { [key: string]: string } = {
      available: 'Tersedia',
      sold: 'Terjual',
      reserved: 'Dipesan',
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!property) {
    return <div>Properti tidak ditemukan</div>;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/properties')}
            style={{ marginRight: 16 }}
          >
            Kembali
          </Button>
          <Title level={2} style={{ margin: 0, display: 'inline' }}>
            {property.title}
          </Title>
          <Badge
            status={property.status === 'available' ? 'success' :
                   property.status === 'sold' ? 'error' : 'warning'}
            text={getStatusText(property.status)}
            style={{ marginLeft: 16 }}
          />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/properties/${id}/edit`)}
          >
            Edit Properti
          </Button>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card>
            {images.length > 0 ? (
              <Carousel autoplay>
                {images.map((image, index) => (
                  <div key={index}>
                    <Image
                      src={image}
                      style={{
                        width: '100%',
                        height: 400,
                        objectFit: 'cover',
                        borderRadius: 8,
                      }}
                      fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23666' font-size='20'%3ENo Image%3C/text%3E%3C/svg%3E"
                    />
                  </div>
                ))}
              </Carousel>
            ) : (
              <div style={{
                height: 400,
                backgroundColor: '#f0f0f0',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}>
                <HomeOutlined style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
                <Text type="secondary">Tidak ada foto</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card>
            <div style={{ marginBottom: 24 }}>
              <Title level={3} style={{ color: '#1890ff', margin: 0 }}>
                {formatCurrency(property.price)}
              </Title>
              <Tag color={getStatusColor(property.status)} style={{ marginTop: 8 }}>
                {getStatusText(property.status)}
              </Tag>
            </div>

            <Descriptions column={1} size="middle">
              <Descriptions.Item label="Tipe Properti">
                <Tag color="blue">{property.property_type?.name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Property Area">
                <Tag color="geekblue">{property.property_area?.Name || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Alamat">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  <div>
                    <div>{property.address}</div>
                    <Text type="secondary">
                      {property.city}{property.province && `, ${property.province}`}
                      {property.postal_code && ` ${property.postal_code}`}
                    </Text>
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Spesifikasi" style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              {property.land_size > 0 && (
                <Col xs={12}>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <HomeOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                    <div style={{ fontWeight: 'bold' }}>{property.land_size}m²</div>
                    <Text type="secondary">Luas Tanah</Text>
                  </div>
                </Col>
              )}
              {property.building_size > 0 && (
                <Col xs={12}>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <HomeOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                    <div style={{ fontWeight: 'bold' }}>{property.building_size}m²</div>
                    <Text type="secondary">Luas Bangunan</Text>
                  </div>
                </Col>
              )}
              {property.bedrooms > 0 && (
                <Col xs={12}>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <HomeOutlined style={{ fontSize: 24, color: '#fa8c16', marginBottom: 8 }} />
                    <div style={{ fontWeight: 'bold' }}>{property.bedrooms}</div>
                    <Text type="secondary">Kamar Tidur</Text>
                  </div>
                </Col>
              )}
              {property.bathrooms > 0 && (
                <Col xs={12}>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <HomeOutlined style={{ fontSize: 24, color: '#13c2c2', marginBottom: 8 }} />
                    <div style={{ fontWeight: 'bold' }}>{property.bathrooms}</div>
                    <Text type="secondary">Kamar Mandi</Text>
                  </div>
                </Col>
              )}
              {property.garage > 0 && (
                <Col xs={12}>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <CarOutlined style={{ fontSize: 24, color: '#722ed1', marginBottom: 8 }} />
                    <div style={{ fontWeight: 'bold' }}>{property.garage}</div>
                    <Text type="secondary">Garasi</Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      </Row>

      {property.description && (
        <Row style={{ marginTop: 24 }}>
          <Col xs={24}>
            <Card title="Deskripsi">
              <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
                {property.description}
              </Paragraph>
            </Card>
          </Col>
        </Row>
      )}

      <Row style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="Informasi Tambahan">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Created By">
                {property.created_by?.full_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tanggal Dibuat">
                {new Date(property.created_at).toLocaleDateString('id-ID', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {new Date(property.updated_at).toLocaleDateString('id-ID', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Property ID">
                #{property.id}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PropertyDetail;